import { Component, inject, input, output, effect } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationAudience, NotificationAudienceTypeMap, NotificationDTO, NotificationType } from '@app/core/models/notification-dto';
import { SVGIcon, checkIcon, eyeIcon, xIcon } from '@progress/kendo-svg-icons';
import { ButtonThemeColor, ButtonComponent } from '@progress/kendo-angular-buttons';
import { _, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgStyle, DatePipe } from '@angular/common';
import { NotificationSeverityIconComponent } from '@app-module/app-controls/notification-severity-icon/notification-severity-icon.component';

_([
  'NotificationMessages.DemandHasBeamMaterial.Title',
  'NotificationMessages.DemandHasBeamMaterial.Text',
  'NotificationMessages.OprimizerRunError.Title',
  'NotificationMessages.OprimizerRunError.Text',
  'NotificationMessages.OprimizerRunSucsess.PressContinuous.Title',
  'NotificationMessages.OprimizerRunSucsess.PressContinuous.Text',
  'NotificationMessages.OprimizerRunSucsess.GlulamSequencer.Title',
  'NotificationMessages.OprimizerRunSucsess.GlulamSequencer.Text',
  'NotificationMessages.OprimizerRunSucsess.AutoScheduler.Title',
  'NotificationMessages.OprimizerRunSucsess.AutoScheduler.Text',
  'NotificationMessages.OprimizerRunSucsess.TacticalLegacy.Title',
  'NotificationMessages.OprimizerRunSucsess.TacticalLegacy.Text',
  'NotificationMessages.OprimizerRunSucsess.PressStatical.Title',
  'NotificationMessages.OprimizerRunSucsess.PressStatical.Text',
  'NotificationMessages.OprimizerRunSucsess.Tactical.Title',
  'NotificationMessages.OprimizerRunSucsess.Tactical.Text',
  'NotificationMessages.BOMInvalidMaterialStatus.Title',
  'NotificationMessages.BOMInvalidMaterialStatus.Text',
  'NotificationMessages.BOMInvalidResponse.Title',
  'NotificationMessages.BOMInvalidResponse.Text',
  'NotificationMessages.SendPOInvalidMaterial.Title',
  'NotificationMessages.SendPOInvalidMaterial.Text',
  'NotificationMessages.SendPOStateMisaligned.Title',
  'NotificationMessages.SendPOStateMisaligned.Text',
  'NotificationMessages.DemandPutAPIErrorMsg.Title',
  'NotificationMessages.DemandPutAPIErrorMsg.Text',
  'NotificationMessages.DemandAPIWarningMsg.Title',
  'NotificationMessages.DemandAPIWarningMsg.Text',
  'NotificationMessages.BOMFailureMessage.Title',
  'NotificationMessages.BOMFailureMessage.Text',
  'NotificationMessages.DemandPOGenerationAPIWarningMsg.Title',
  'NotificationMessages.DemandPOGenerationAPIWarningMsg.Text',
  'NotificationMessages.ProdOrderSuccessMsg.Title',
  'NotificationMessages.ProdOrderSuccessMsg.Text',
  'Notification.ViewDocument',
]);

@Component({
  selector: 'app-notification-item',
  templateUrl: './notification-item.component.html',
  imports: [NgStyle, NotificationSeverityIconComponent, ButtonComponent, DatePipe, TranslatePipe],
})
export class NotificationItemComponent {
  notification = input.required<NotificationDTO>();
  actionIcon = input.required<SVGIcon>();
  actionTitle = input.required<string>();
  navigateTitle = input<string>('Notification.ViewDocument');
  actionThemeColor = input.required<ButtonThemeColor>();
  action = output<void>();
  close = output<void>();
  notificationUserType: typeof NotificationAudience = NotificationAudience;
  notificationUserTypeMap = NotificationAudienceTypeMap;

  checkIcon: SVGIcon = checkIcon;
  xIcon: SVGIcon = xIcon;
  eyeIcon: SVGIcon = eyeIcon;

  translatedTitle = '';
  translatedText = '';

  private router = inject(Router);
  private translateService = inject(TranslateService);

  constructor() {
    effect(() => {
      if (this.notification().hasTranslation) {
        this.translatedTitle = this.translateService.instant(`NotificationMessages.${this.notification().title}.Title`, this.notification().translationData);
        this.translatedText = this.translateService.instant(`NotificationMessages.${this.notification().title}.Text`, this.notification().translationData);
      } else {
        this.translatedTitle = this.notification().title;
        this.translatedText = this.notification().text;
      }
    });
  }

  onAction(): void {
    this.action.emit();
  }

  getSeverityBorderStyle(severity: number): { [key: string]: string } {
    const styles: { [key: number]: { border: string; background: string } } = {
      0: { border: '#0059e9b8', background: '#e6f0fd' }, // info
      1: { border: '#ffbf00bb', background: '#fff8e6' }, // warning
      2: { border: '#f31700', background: '#fde8e6' }, // error
      3: { border: '#f31700', background: '#fde8e6' }, // critical
      4: { border: '#37b400', background: '#ebf6e6' }, // success
    };
    const style = styles[severity] || styles[0];
    return {
      border: `2px solid ${style.border}`,
      'background-color': style.background,
      'border-radius': '5px',
    };
  }

  navigateToDocument() {
    if (this.notification().type === NotificationType.DEMAND_ISSUE) {
      this.router.navigate(['/aot/demand', this.notification().docRefGuid]);
    } else if (this.notification().type === NotificationType.ENTITY_ISSUE) {
      this.router.navigate(['/model/entity/', this.notification().docRefGuid]);
    } else if (this.notification().type === NotificationType.PRODORDER_ISSUE) {
      this.router.navigate(['/operative/prod-order/', this.notification().docRefInt]); // Until we have a guid-based route
    }

    this.close.emit();
  }
}
